import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, XIcon } from 'lucide-react';
import { storyboardDeepDives, storyboardFrames, storyboardMeta, type StoryboardFrame } from '../../data/storyboard';
import { snakDisclaimer } from '../../data/snak';
const EASE = [0.23, 1, 0.32, 1] as const;
interface StoryboardOverlayProps {
  onClose: () => void;
}
function FrameCard({
  frame
}: {
  frame: StoryboardFrame;
}) {
  return <article className="print-break flex flex-col overflow-hidden rounded-2xl border border-line bg-paper" data-id="element-266" data-mp="234hnsunnu5vg">
      <div className="flex items-baseline justify-between gap-3 bg-navy px-4 py-2.5" data-id="element-267" data-mp="2829ks335wi6i">
        <span className="font-display text-[12px] font-bold tabular-nums text-mint" data-id="element-268" data-mp="1tdn1jqaez6a0">
          {frame.index}
        </span>
        <span className="flex-1 text-[9px] uppercase tracking-micro text-sky/75" data-id="element-269" data-mp="1yawpt2m2hbw9">
          {frame.chapter}
        </span>
        <span className="text-[9px] tabular-nums text-sky/60" data-id="element-270" data-mp="2qtag8h52pvlb">{frame.seconds}</span>
      </div>

      {frame.images ? <div className="grid grid-cols-4 gap-px bg-line" data-id="element-271" data-mp="132f7rhrl8o6o">
          {frame.images.map(src => <img key={src} src={src} alt="" aria-hidden="true" className="h-16 w-full bg-ink object-cover object-center" data-id="element-272" data-mp="y4x1y08vap2w" />)}
        </div> : frame.image ? <img src={frame.image} alt="" aria-hidden="true" className="h-28 w-full bg-ink object-cover object-center" data-id="element-273" data-mp="172z6gprt7zux" /> : <div className="flex h-28 items-center justify-center bg-mist px-5" data-id="element-274" data-mp="1gudexn1qoa07">
          <p className="text-center text-[10px] uppercase tracking-micro text-mut" data-id="element-275" data-mp="1ut3bn1knc7o1">
            Single select cue · no right/wrong feedback
          </p>
        </div>}

      <div className="flex flex-1 flex-col px-5 py-4" data-id="element-276" data-mp="ftzt34moa2m">
        <h3 className="font-display text-[15px] font-semibold leading-snug text-navy" data-id="element-277" data-mp="ig66zfs8jj9">
          {frame.onScreen}
        </h3>
        <p className="mt-2 text-[11.5px] leading-relaxed text-mut" data-id="element-278" data-mp="1vjrzkexcw02t">{frame.detail}</p>

        <dl className="mt-4 space-y-2.5 border-t border-line pt-3" data-id="element-279" data-mp="20imqk45h8uy0">
          <div data-id="element-280" data-mp="20iv500h908bb">
            <dt className="text-[8.5px] uppercase tracking-micro text-mut" data-id="element-281" data-mp="20kphcqeges3s">HCP</dt>
            <dd className="mt-0.5 text-[11.5px] leading-snug text-navy" data-id="element-282" data-mp="25gegismka6zk">{frame.hcp}</dd>
          </div>
          <div data-id="element-283" data-mp="2ai3w8lkqb8tz">
            <dt className="text-[8.5px] uppercase tracking-micro text-blue" data-id="element-284" data-mp="2aeg1xk7gmie7">Rep says</dt>
            <dd className="mt-0.5 text-[11.5px] leading-snug text-navy" data-id="element-285" data-mp="2egbb1avmu02j">{frame.repSays}</dd>
          </div>
          <div data-id="element-286" data-mp="2jf4eeah7gaoz">
            <dt className="text-[8.5px] uppercase tracking-micro text-mut" data-id="element-287" data-mp="2o8h28ie28dgo">Purpose</dt>
            <dd className="mt-0.5 text-[11.5px] leading-snug text-mut" data-id="element-288" data-mp="2oajt14n1eamg">{frame.purpose}</dd>
          </div>
        </dl>
      </div>
    </article>;
}
export function StoryboardOverlay({
  onClose
}: StoryboardOverlayProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="print-sheet fixed inset-0 z-50 flex items-center justify-center bg-ink/70 px-6 py-8" role="dialog" aria-modal="true" aria-label={storyboardMeta.title} data-id="element-289" data-mp="32770w2gj2l75">
      <motion.div initial={{
      opacity: 0,
      y: 10,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.98
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="print-plain flex max-h-full w-full max-w-[1120px] flex-col overflow-hidden rounded-[28px] bg-paper shadow-soft" data-id="element-290" data-mp="hm19foy3bthi">
        <div className="print-hidden flex items-center justify-between gap-6 bg-navy px-8 py-4" data-id="element-291" data-mp="10i17nhhnvt9j">
          <p className="flex items-center gap-2.5 text-[10px] uppercase tracking-micro text-mint" data-id="element-292" data-mp="vkhny2272oa7">
            <span className="h-1.5 w-1.5 rounded-full bg-mint" aria-hidden="true" data-id="element-293" data-mp="1szdkokw6jedp" />
            {storyboardMeta.eyebrow}
          </p>
          <div className="flex items-center gap-2" data-id="element-294" data-mp="1o72j7dlsjkj1">
            <button type="button" onClick={() => window.print()} className="flex h-10 items-center gap-2 rounded-full bg-mint/20 px-4 text-[12.5px] font-medium text-mint transition-opacity duration-150 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-295" data-mp="18pud0dv4k224">
              <DownloadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-296" data-mp="13rj6ac04m7o4" />
              Download PDF
            </button>
            <button type="button" onClick={onClose} aria-label="Close storyboard" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-sky/25 text-sky/80 transition-colors duration-150 hover:border-mint/50 hover:text-mint focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-297" data-mp="1rj7rl8ej5vvk">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-298" data-mp="2admu3801w6o5" />
            </button>
          </div>
        </div>

        <div className="print-scroll overflow-y-auto px-8 py-7" data-id="element-299" data-mp="2f8987ixiodbx">
          <header className="print-break" data-id="element-300" data-mp="3pw97c0003dp">
            <h2 className="font-display text-[28px] font-semibold leading-tight tracking-tight text-navy" data-id="element-301" data-mp="3uxs0ilkai0x3">
              {storyboardMeta.title}
            </h2>
            <p className="mt-1.5 text-[12.5px] text-mut" data-id="element-302" data-mp="cizhl5vsdq1h">{storyboardMeta.subtitle}</p>
            <p className="mt-3 border-l-2 border-mint pl-3 font-display text-[15px] leading-snug text-navy" data-id="element-303" data-mp="8jgzsiz9hv2v">
              {storyboardMeta.runtime}
            </p>
          </header>

          <div className="print-grid mt-7 grid gap-4 lg:grid-cols-3" data-id="element-304" data-mp="3bzxsoauwdvga">
            {storyboardFrames.map(frame => <FrameCard key={frame.id} frame={frame} data-id="element-305" data-mp="3q08kbg417e2i" />)}
          </div>

          <h3 className="print-break mt-9 border-b border-line pb-2 text-[11px] uppercase tracking-micro text-navy" data-id="element-306" data-mp="3kysy3kq4b1ij">
            Optional deep dives · HCP request only
          </h3>
          <div className="print-grid mt-4 grid gap-4 lg:grid-cols-2" data-id="element-307" data-mp="10a4updace483">
            {storyboardDeepDives.map(frame => <FrameCard key={frame.id} frame={frame} data-id="element-308" data-mp="3u0jsuet8thw1" />)}
          </div>

          <p className="print-break mt-8 border-t border-line pt-4 text-[10.5px] leading-relaxed text-mut" data-id="element-309" data-mp="2tz4x3yhiq38">
            {snakDisclaimer}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}